export const XHR_CONTENT_TYPE = {
    JSON: "JSON",
    FormData: "FormData"
};

export const XHR_RESULT_TYPE = {
    JSON: "JSON",
    TEXT: "TEXT"
};

/**
 * Boolean that will be true if the full solution is built in VS in the "debug"
 * configuration, or if the WebUI project is built with "yarn run dev" or "yarn
 * run dev-watch". Should be used sparingly to avoid differences in debug-build code and
 * release-build code. This is intended for things like showing errors or warnings
 * to developers if they misuse a shared component.
 */
export const IS_DEBUG_BUILD = process.env.NODE_ENV !== "production";

export function escapeRegex(str) {
    return str.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, "\\$&");
}

export function htmlUnescape(value) {
    if (!value) {
        return null;
    }
    return String(value)
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'")
        .replace(/&lt;/g, "<")
        .replace(/&gt;/g, ">")
        .replace(/&amp;/g, "&");
}

export function deepClone(obj) {
    return JSON.parse(JSON.stringify(obj));
}

export function isEmptyOrSpaces(str) {
    return str === undefined || str === null || str.toString().match(/^\s*$/) !== null;
}

/**
 * Returns true IFF typeof obj === "number" and the obj is finite. Does NOT do type coersions
 * @param {*} obj object to check
 */
export function isFiniteNumber(obj) {
    return Number.isFinite(obj);
}

/**
 * Returns the finite number reprented by obj to a finite number. Otherwise returns NaN.
 * If obj is undefined, null, "", or [], this function will return NaN.
 * @param {*} obj object to convert
 */
export function convertToFiniteNumber(obj) {
    if (obj === undefined || obj === null || obj === "" || obj === []) {
        return NaN;
    }
    const val = Number(obj);
    if (isFiniteNumber(val)) {
        return val;
    }
    return NaN;
}

export function convertValueToString(value) {
    if (value === null || value === undefined) {
        value = "";
    }
    //value should always be a string
    if (typeof value !== "string") {
        value = value.toString();
    }
    return value;
}

/**
 * Decimal formula results are considered equal if they differ by
 * an absolute value less than this margin
 */
const DECIMAL_RESULT_EQUALITY_ERROR_MARGIN = 0.00000001;

/**
 * Because JS floating point arithmetic is imprecise, we do a
 * "fuzzy compare" and treat calculated numeric values as equal if
 * they differ by a very small amount
 * @param {*} storedValue
 * @param {*} evaluatedValue
 */
export function areCalculatedDecimalValuesEqual(storedValue, evaluatedValue) {
    const storedValueNum = convertToFiniteNumber(storedValue);
    const evaluatedValueNum = convertToFiniteNumber(evaluatedValue);

    // If stored and calculated are both valid finite numbers, do a fuzzy
    // numeric compare. Otherwise fall back to string value compare
    if (isFiniteNumber(storedValueNum) && isFiniteNumber(evaluatedValueNum)) {
        return Math.abs(storedValueNum - evaluatedValueNum) < DECIMAL_RESULT_EQUALITY_ERROR_MARGIN;
    } else {
        return convertValueToString(storedValue) === convertValueToString(evaluatedValue);
    }
}

export function safeSetItem(key, value) {
    try {
        localStorage.setItem(key, value);
    } catch (e) {
        //do nothing, just log
        console.error(`failed to set item ${key} to value${value} in local storage`);
    }
}

export function downloadFile(url, anonParams) {
    if (typeof anonParams === "string") {
        url = url + anonParams;
    }
    const a = document.createElement("a");
    a.download = "";
    a.href = url;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
}

export function getAnonParams(anonToken, anonNonce) {
    return `&AnonToken=${encodeURIComponent(anonToken)}&AnonNonce=${encodeURIComponent(anonNonce)}`;
}

export function exportTextToFile(txt, filename, mimetype) {
    const type = mimetype || "text/plain;charset=utf-8";
    const blob = new Blob([txt], { type });
    const url = URL.createObjectURL(blob);

    const downloadLink = document.createElement("a");
    downloadLink.href = url;
    downloadLink.download = filename;
    downloadLink.target = "_blank";
    downloadLink.style.display = "none";

    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
    setTimeout(() => {
        URL.revokeObjectURL(url);
    }, 10);
}

export const DOCUMENT_WRAPPER_TYPE = {
    DocumentId: "DocumentId",
    AttachmentId: "AttachmentId",
    GeneratedDocumentTemplateId: "GeneratedDocumentTemplateId",
    SignatoryId: "SignatoryId",
    ESignatureElementStatusId: "ESignatureElementStatusId",
    PendingReportId: "PendingReportId"
};

export function isIE() {
    const ua = window.navigator.userAgent.toLowerCase();
    return /msie|trident/.test(ua);
}

export function textAreaToList(text) {
    if (!text) {
        return [];
    }
    const lines = text.match(/[^\r\n]+/g);
    if (!lines) {
        return [];
    }
    return lines
        .filter(function (line) {
            return line != null && !/^\s+$/.test(line);
        })
        .map(function (line) {
            return line.replace(/\s+/g, " ").trim();
        });
}

export function loadDynamicScript(src) {
    let _reject;
    let _resolve;
    const promise = new Promise((resolve, reject) => {
        _resolve = resolve;
        _reject = reject;
    });

    const scriptElement = document.createElement("script");
    let timeout;

    const clearHooks = function () {
        scriptElement.onerror = null;
        scriptElement.onload = null;
        clearTimeout(timeout);
    };

    scriptElement.src = src;

    timeout = setTimeout(() => {
        clearHooks();
        _reject(`Timeout while trying to load script: ${src}`);
    }, 60000); // wait for one minute

    scriptElement.onerror = () => {
        clearHooks();
        _reject(`Error loading script: ${src}`);
    };
    scriptElement.onload = () => {
        clearHooks();
        _resolve();
    };
    document.head.appendChild(scriptElement);
    return promise;
}

/**
 * Collapses whitespace in input. If input is not a string, it does nothing and returns the input unchanged.
 * @param {String} input
 */
export function collapseAndTrimWhitespace(input) {
    if (typeof input !== "string") {
        return input;
    }
    // Collapses all instances of one or more whitespaces (\s+) down to a single whitespace.
    const collapsed = input.replace(/\s+/g, " ");
    const trimmed = collapsed.trim(); // Removes whitespaces at either end
    return trimmed;
}

/**
 * Compares two strings for equality. If either parameter is not a string, this will return false
 * @param {String} str1
 * @param {String} str2
 */
export function areStringsEqualIgnoreCase(str1, str2) {
    if (typeof str1 !== "string" || typeof str2 !== "string") {
        return false;
    }
    return str1.toLowerCase() === str2.toLowerCase();
}

/**
 * Simple comparer function to be used for sorting arrays of numeric values
 */
export function numericCompare(a, b) {
    return a < b ? -1 : a > b ? 1 : 0;
}

export function simpleDebounce(func, wait) {
    let timeout;
    return function () {
        const context = this;
        const args = arguments;

        const debounced = function () {
            func.apply(context, args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(debounced, wait);
    };
}

/**
 * Split collection into batches limited by size.
 * @param {Array} items Collection of items.
 * @param {Number} batchSize Maximum size of batch.
 * @returns An array containing batch arrays of items.
 */
export function batchItems(items, batchSize) {
    if (!items) {
        return [];
    }

    const batches = [];
    let i = 0;
    let batch = [];

    while (i < items.length) {
        batch.push(items[i]);
        if (batch.length >= batchSize) {
            batches.push(batch);
            batch = [];
        }
        i++;
    }

    // Make sure to include last partially full batch
    if (batch.length > 0) {
        batches.push(batch);
    }
    return batches;
}

export function getDomainName(url) {
    // Remove the subdomain from the domain name if it exists
    const hostName = new URL(url).hostname;
    const hostComponents = hostName.split(".");
    if (hostComponents.length === 3) {
        hostComponents.shift();
    }
    return hostComponents.join(".");
}

export function scrollToElement(elementId) {
    const targetElement = document.getElementById(elementId);
    if (!targetElement) {
        return;
    }
    let scrollableParent = document.getElementById("NavLayoutPageContent");
    if (!scrollableParent) {
        scrollableParent = targetElement.closest("body");
    }
    if (!scrollableParent || scrollableParent.scrollHeight === scrollableParent.clientHeight) {
        scrollableParent = targetElement.closest("html");
    }
    if (!scrollableParent) {
        // element did not have a scrollable parent, throw
        throw "Expected scrollable parent not found for targeted element during scroll-to action.";
    }
    // NavLayoutPageContent contains the page content and will scroll instead of the page.
    // Calculate where the viewport needs to move to contain the target
    const targetScrollTop =
        scrollableParent.scrollTop +
        (targetElement.getBoundingClientRect().top - scrollableParent.getBoundingClientRect().top - 55);
    scrollableParent.scrollTo({ top: targetScrollTop, behavior: "smooth" });
}

export async function timeout(ms) {
    await new Promise((resolve) => setTimeout(resolve, ms));
}
/**
 * Get boolean value of a property of a provided element
 * @param {String} propName name of property to get the boolean value of.
 * @param {*} element with property "propName" to get the boolean value of.
 * @returns A boolean of whether this property of element is "true". Returns false if
 * the property is undefined.
 */
export function getBooleanPropertyValue(propName, element) {
    const propVal = element.Properties[propName] || "";
    return propVal.toLowerCase() === "true";
}

export async function hashStringSha256(input) {
    // Encode to utf-8, hash the message, convert buffer to byte array, convert bytes to hex string
    const msgUint8 = new TextEncoder().encode(input);
    const hashBuffer = await crypto.subtle.digest("SHA-256", msgUint8);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
    return hashHex;
}
